import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Shield, Save, RotateCcw } from "lucide-react";
import { recruitingAPI } from "../services/recruitingApi";
import "./GdprConfigPanel.css";

interface GdprConfig {
  gdpr_form_type: string;
  gdpr_consent_form_title: string;
  gdpr_consent_form_intro: string;
  gdpr_consent_form_processing_text: string;
  gdpr_consent_form_rights_text: string;
  gdpr_consent_form_retention_text: string;
  gdpr_withdrawal_form_warning: string;
  gdpr_withdrawal_form_confirmation: string;
}

interface GdprConfigResponse {
  success: boolean;
  data: GdprConfig;
}

const DEFAULT_CONFIG: GdprConfig = {
  gdpr_form_type: "internal",
  gdpr_consent_form_title: "GDPR Consent",
  gdpr_consent_form_intro:
    "We need your consent to process your personal data for recruitment purposes.",
  gdpr_consent_form_processing_text:
    "Your data will be processed for recruitment activities including candidate assessment, interview scheduling, and job matching.",
  gdpr_consent_form_rights_text:
    "You have the right to access, rectify, and delete your personal data. You can withdraw consent at any time.",
  gdpr_consent_form_retention_text:
    "Your data will be retained for 2 years unless you request earlier deletion.",
  gdpr_withdrawal_form_warning:
    "Withdrawing consent will remove you from active recruitment processes. Your data will be anonymized within 30 days.",
  gdpr_withdrawal_form_confirmation:
    "I understand the consequences and wish to withdraw my consent.",
};

const CONFIG_LABELS: Record<
  keyof GdprConfig,
  { label: string; description: string; type: "text" | "textarea" | "select" }
> = {
  gdpr_form_type: {
    label: "Consent Form Type",
    description:
      "Choose whether to use the built-in consent form or redirect to an external URL.",
    type: "select",
  },
  gdpr_consent_form_title: {
    label: "Consent Form Title",
    description: "The title displayed at the top of the consent form.",
    type: "text",
  },
  gdpr_consent_form_intro: {
    label: "Introduction Text",
    description: "The introductory message explaining why consent is needed.",
    type: "textarea",
  },
  gdpr_consent_form_processing_text: {
    label: "Data Processing Text",
    description: "Explanation of how candidate data will be processed.",
    type: "textarea",
  },
  gdpr_consent_form_rights_text: {
    label: "Data Subject Rights Text",
    description: "Information about the candidate's rights under GDPR.",
    type: "textarea",
  },
  gdpr_consent_form_retention_text: {
    label: "Data Retention Text",
    description: "How long data will be retained.",
    type: "textarea",
  },
  gdpr_withdrawal_form_warning: {
    label: "Withdrawal Warning Text",
    description:
      "Warning message shown when a candidate tries to withdraw consent.",
    type: "textarea",
  },
  gdpr_withdrawal_form_confirmation: {
    label: "Withdrawal Confirmation Text",
    description:
      "The checkbox text candidates must confirm to withdraw consent.",
    type: "textarea",
  },
};

const GdprConfigPanel: React.FC = () => {
  const { t } = useTranslation(["admin", "common"]);
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<GdprConfig>(DEFAULT_CONFIG);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch GDPR config
  const { data: configData, isLoading } = useQuery<GdprConfigResponse>(
    "gdpr-config",
    () => recruitingAPI.getGdprConfig() as Promise<GdprConfigResponse>,
  );

  // Save config mutation
  const saveConfigMutation = useMutation(
    (config: Record<string, string>) => recruitingAPI.setGdprConfig(config),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("gdpr-config");
        setHasChanges(false);
        alert("GDPR configuration saved successfully!");
      },
      onError: (error: Error) => {
        alert("Error saving configuration: " + error.message);
      },
    },
  );

  // Update form when config loads
  useEffect(() => {
    if (configData?.data) {
      setFormData(configData.data);
    }
  }, [configData]);

  const handleChange = (key: keyof GdprConfig, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    saveConfigMutation.mutate(formData as unknown as Record<string, string>);
  };

  const handleReset = () => {
    if (window.confirm(t("admin:gdpr.resetConfirm"))) {
      setFormData(DEFAULT_CONFIG);
      setHasChanges(true);
    }
  };

  if (isLoading) {
    return (
      <div className="gdpr-config-loading">
        <div className="spinner"></div>
        <p>Loading GDPR configuration...</p>
      </div>
    );
  }

  return (
    <div className="gdpr-config-panel">
      <div className="config-header">
        <div className="header-info">
          <Shield size={24} />
          <div>
            <h3>GDPR Consent Form Configuration</h3>
            <p>
              Customize the text and settings for GDPR consent and withdrawal
              forms.
            </p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={handleReset}
            disabled={saveConfigMutation.isLoading}
          >
            <RotateCcw size={16} /> Reset to Defaults
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!hasChanges || saveConfigMutation.isLoading}
          >
            <Save size={16} />{" "}
            {saveConfigMutation.isLoading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {hasChanges && (
        <div className="unsaved-changes-banner">
          You have unsaved changes. Click "Save Changes" to apply them.
        </div>
      )}

      <div className="config-sections">
        {/* Consent Form Section */}
        <div className="config-section">
          <h4>Consent Form Settings</h4>

          {(
            [
              "gdpr_form_type",
              "gdpr_consent_form_title",
              "gdpr_consent_form_intro",
              "gdpr_consent_form_processing_text",
              "gdpr_consent_form_rights_text",
              "gdpr_consent_form_retention_text",
            ] as (keyof GdprConfig)[]
          ).map((key) => {
            const config = CONFIG_LABELS[key];
            return (
              <div key={key} className="config-field">
                <label htmlFor={key}>
                  {config.label}
                  <span className="field-description">
                    {config.description}
                  </span>
                </label>
                {config.type === "select" ? (
                  <select
                    id={key}
                    value={formData[key]}
                    onChange={(e) => handleChange(key, e.target.value)}
                  >
                    <option value="internal">Internal Form (Built-in)</option>
                    <option value="external">
                      External Form (Redirect URL)
                    </option>
                  </select>
                ) : config.type === "textarea" ? (
                  <textarea
                    id={key}
                    value={formData[key]}
                    onChange={(e) => handleChange(key, e.target.value)}
                    rows={3}
                  />
                ) : (
                  <input
                    type="text"
                    id={key}
                    value={formData[key]}
                    onChange={(e) => handleChange(key, e.target.value)}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Withdrawal Form Section */}
        <div className="config-section">
          <h4>Withdrawal Form Settings</h4>

          {(
            [
              "gdpr_withdrawal_form_warning",
              "gdpr_withdrawal_form_confirmation",
            ] as (keyof GdprConfig)[]
          ).map((key) => {
            const config = CONFIG_LABELS[key];
            return (
              <div key={key} className="config-field">
                <label htmlFor={key}>
                  {config.label}
                  <span className="field-description">
                    {config.description}
                  </span>
                </label>
                <textarea
                  id={key}
                  value={formData[key]}
                  onChange={(e) => handleChange(key, e.target.value)}
                  rows={3}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GdprConfigPanel;
